import isNil from 'lodash/isNil'
import { trimLeftAndLowerCaseString } from './string'

const nullSafeStringCompare = makeComparatorNullSafe((str1, str2) => {
    return str1.localeCompare(str2)
})

/**
 * Generates an object compare function considering the given property of type string (case-insensitive by default).
 * Non-existent objects are considered greater than existent objects in comparison. The property may be transformed
 * before comparison when a transform callback function is given.
 *
 * @param {string} propertyName - The property that is the base for comparison.
 * @param {function} [transformCallback] - Callback for property transformation. May also transform a non-string
 *                                         property into a string.
 *                                         Signature: (property) => transformedPropertyAsString
 * @param {boolean} [caseSensitive] - Compare property case-sensitive.
 * @returns {function} Compare function to be used in Array.prototype.sort that returns a compare value greater, equal,
 *                     or above 0.
 */
export function getObjectCompareByPropertyFunction(propertyName, transformCallback = null, caseSensitive = false) {
    return makeComparatorNullSafe((object1, object2) => {
        let property1, property2
        if (transformCallback && typeof transformCallback === 'function') {
            property1 = transformCallback(object1[propertyName]) || ''
            property2 = transformCallback(object2[propertyName]) || ''
        } else {
            property1 = object1[propertyName] ? String(object1[propertyName]) : ''
            property2 = object2[propertyName] ? String(object2[propertyName]) : ''
        }
        property1 = caseSensitive ? property1 : property1.toLowerCase()
        property2 = caseSensitive ? property2 : property2.toLowerCase()
        return property1.localeCompare(property2)
    })
}

/**
 * Creates a comparator function which checks two values on their validity and calls an actual comparison function, if
 * both values are valid. Invalid values are sorted to the end.
 *
 * @param {Function} checkValueInvalidity - Function to check both values for validity.
 * @param {Function} comparator - The comparison function which gets called, when both values are valid.
 * @param {boolean} invalidToEnd - A flag whether invalid values should be at the and or at the beginning.
 * @return {Function} The comparison function.
 */
export function makeSafeComparator(checkValueInvalidity, comparator, invalidToEnd = true) {
    return (value1, value2) => {
        if (checkValueInvalidity(value1) && checkValueInvalidity(value2)) {
            return 0
        } else if (checkValueInvalidity(value1)) {
            return invalidToEnd ? 1 : 1
        } else if (checkValueInvalidity(value2)) {
            return invalidToEnd ? -1 : 1
        } else {
            return comparator(value1, value2)
        }
    }
}

/**
 * Creates a null-safe comparator from a given one. Means that the given comparator can relay on the fact
 * that any of the arguments it will get can be treated as not null and not undefined.
 *
 * @param {Function} comparator - A comparator.
 * @returns {Function} The null-safe comparator.
 */
export function makeComparatorNullSafe(comparator) {
    return makeSafeComparator(isNil, comparator)
}

/**
 * Compares two date timestamps and returns a compare value greater, equal, or above 0.
 *
 * @param {number} timestampA
 * @param {number} timestampB
 * @returns {number}
 */
export function compareDates(timestampA, timestampB) {
    if (isNaN(timestampA) || isNaN(timestampB)) {
        return 0
    }
    return timestampA - timestampB
}

/**
 * A function for comparing two strings with null check using localeCompare.
 *
 * @param {string} str1 - The first string
 * @param {string} str2 - The second string
 * @returns {number} The sorting index
 */
export function compareStrings(str1, str2) {
    return nullSafeStringCompare(str1, str2)
}

/**
 * Sorting function for strings that does a left trim and lower case conversion before comparing. ToLowerCase helps to
 * prevent extra sorting of big and small names.
 *
 * @example
 * ['z', '  k', ' a '].sort(compareStrings) === [' a ', '  k', 'z']
 *
 * @param {string} stringA - The first string
 * @param {string} stringB - The second string
 * @returns {number} The sorting index
 */
export function compareStringsLeftTrimmedAndCaseInsensitive(stringA, stringB) {
    const trimmedStringA = trimLeftAndLowerCaseString(stringA)
    const trimmedStringB = trimLeftAndLowerCaseString(stringB)

    return compareStrings(trimmedStringA, trimmedStringB)
}

/**
 * A number sort function
 *
 * @param {number} num1
 * @param {number} num2
 * @returns {number}
 */
export function sortNumber(num1, num2) {
    return num1 - num2
}

/**
 * Sort objects by any given datetime prop.
 *
 * @param {Object} a - Any object with a property of the given datetime
 * @param {Object} b - Any object with a property  of the given datetime
 * @param {string} dateTimeProp - The datetime property to be sorted
 * @returns {number} The sorting order
 */
export function sortByDateTime(a, b, dateTimeProp) {
    if (!dateTimeProp) {
        return 0
    }

    const num1 = new Date(a[dateTimeProp]).getTime()
    const num2 = new Date(b[dateTimeProp]).getTime()
    return sortNumber(num1, num2)
}
